/* globals FWP, FWP_JSON, FWP_MAP, google, strl_vars */

/**
 * Lazy-loads Google Maps for FacetWP proximity facets (and shared consumers).
 *
 * FacetWP normally loads the full Google Maps JS API (~200 KB+) on every page
 * that contains a proximity facet. This component defers loading until the user
 * actually interacts with the location field or the locate-me button, then
 * replicates the autocomplete init that FacetWP would have done on first load.
 *
 * Pairs with the `facetwp_proximity_load_js` → false filter in facetwp.php
 * and the gmaps_url value injected into FWP_JSON.proximity / strl_vars.
 */

window.__strlGmapsLoadState = window.__strlGmapsLoadState || 'idle';
window.__strlGmapsCallbacks = window.__strlGmapsCallbacks || [];

function getGmapsUrl() {
	if (typeof FWP_JSON !== 'undefined' && FWP_JSON.proximity?.gmaps_url) {
		return FWP_JSON.proximity.gmaps_url;
	}

	if (typeof strl_vars !== 'undefined' && strl_vars.gmaps_url) {
		return strl_vars.gmaps_url;
	}

	return '';
}

function flushGmapsCallbacks() {
	const callbacks = window.__strlGmapsCallbacks.splice(0);

	callbacks.forEach((callback) => {
		callback();
	});
}

/**
 * Request Google Maps (Places) and run callback when ready.
 * Shared with Gravity Forms address autocomplete to avoid double-loading.
 *
 * @param {Function} [callback]
 */
export function requestGoogleMaps(callback) {
	if (typeof callback === 'function') {
		if (typeof google !== 'undefined' && google.maps) {
			callback();
			return;
		}

		window.__strlGmapsCallbacks.push(callback);
	}

	loadGoogleMaps();
}

let facetwpPlacesInited = false;

function loadGoogleMaps() {
	if (window.__strlGmapsLoadState === 'loaded') {
		flushGmapsCallbacks();
		return;
	}

	if (window.__strlGmapsLoadState === 'loading') {
		return;
	}

	if (typeof google !== 'undefined' && google.maps) {
		window.__strlGmapsLoadState = 'loaded';
		flushGmapsCallbacks();
		if (!facetwpPlacesInited) {
			facetwpPlacesInited = true;
			initFacetwpPlaces();
		}
		return;
	}

	const url = getGmapsUrl();
	if (!url) {
		return;
	}

	window.__strlGmapsLoadState = 'loading';

	const script = document.createElement('script');
	script.src = url + '&callback=__strlGmapsReady';
	script.async = true;
	script.onerror = () => {
		window.__strlGmapsLoadState = 'idle';
	};
	document.head.appendChild(script);
}

window.__strlGmapsReady = async function () {
	window.__strlGmapsLoadState = 'loaded';
	flushGmapsCallbacks();

	if (!facetwpPlacesInited) {
		facetwpPlacesInited = true;
		initFacetwpPlaces();
	}
};

function initFacetwpPlaces() {
	if (typeof FWP === 'undefined' || typeof google === 'undefined') {
		return;
	}

	if (typeof FWP_JSON === 'undefined' || !FWP_JSON.proximity) {
		return;
	}

	window.FWP_MAP = window.FWP_MAP || {};
	const opts = FWP_JSON.proximity || {};

	if ('place-class' === FWP.settings?.places) {
		initNewPlacesAPI(opts);
	} else {
		initLegacyPlacesAPI(opts);
	}
}

// — New Places API (place-class) ——————————————————————————————————

async function initNewPlacesAPI(opts) {
	try {
		const { AutocompleteSessionToken, AutocompleteSuggestion } =
			await google.maps.importLibrary('places');

		let timeout;
		document.addEventListener('input', (e) => {
			if (!e.target.classList.contains('facetwp-location')) return;
			clearTimeout(timeout);
			timeout = setTimeout(() => {
				newPlacesAutocomplete(e.target, AutocompleteSessionToken, AutocompleteSuggestion, opts);
			}, opts.queryDelay || 250);
		});
	} catch (err) {
		console.error('lazy-gmaps:', err);
	}
}

function newPlacesAutocomplete(input, SessionToken, Suggestion, opts) {
	const val = input.value;
	const facet = input.closest('.facetwp-facet');
	const results = facet?.querySelector('.location-results');

	if (!val || val.length < (opts.minLength || 3)) {
		results?.classList.add('facetwp-hidden');
		return;
	}

	FWP_MAP.token ||= new SessionToken();

	Suggestion.fetchAutocompleteSuggestions({
		input: val,
		sessionToken: FWP_MAP.token,
		...(opts.autocomplete_options || {}),
	}).then(({ suggestions }) => {
		if (!suggestions?.length) {
			if (results) {
				results.innerHTML = '';
				results.classList.add('facetwp-hidden');
			}
			return;
		}

		let html = '';
		suggestions.forEach((s, i) => {
			const r = s.placePrediction;
			const css = i === 0 ? ' active' : '';
			html += '<div class="location-result' + css + '" data-place-id="' + r.placeId + '" data-index="' + i + '">';
			html += '<span class="result-main">' + (r.mainText?.toString() ?? '') + '</span> ';
			html += '<span class="result-secondary">' + (r.secondaryText?.toString() ?? '') + '</span>';
			html += '<span class="result-description facetwp-hidden">' + (r.text?.toString() ?? '') + '</span>';
			html += '</div>';
		});
		html += '<div class="location-attribution"><div class="powered-by-google"></div></div>';

		if (results) {
			results.innerHTML = html;
			results.classList.remove('facetwp-hidden');
		}
	});
}

// — Legacy Places API (places-service) ————————————————————————————

async function initLegacyPlacesAPI(opts) {
	try {
		await google.maps.importLibrary('places');

		FWP_MAP.sessionToken = new google.maps.places.AutocompleteSessionToken();
		FWP_MAP.autocompleteService = new google.maps.places.AutocompleteService();
		FWP_MAP.placesService = new google.maps.places.PlacesService(document.createElement('div'));

		let timeout;
		document.addEventListener('input', (e) => {
			if (!e.target.classList.contains('facetwp-location')) return;
			clearTimeout(timeout);
			timeout = setTimeout(() => {
				legacyPlacesAutocomplete(e.target, opts);
			}, opts.queryDelay || 250);
		});
	} catch (err) {
		console.error('lazy-gmaps:', err);
	}
}

function legacyPlacesAutocomplete(input, opts) {
	const val = input.value;
	const facet = input.closest('.facetwp-facet');
	const results = facet?.querySelector('.location-results');

	if (!val || val.length < (opts.minLength || 3)) {
		results?.classList.add('facetwp-hidden');
		return;
	}

	FWP_MAP.autocompleteService.getPredictions(
		{
			...(opts.autocomplete_options || {}),
			sessionToken: FWP_MAP.sessionToken,
			input: val,
		},
		(predictions, status) => {
			if (status !== google.maps.places.PlacesServiceStatus.OK) return;

			let html = '';
			predictions.forEach((r, i) => {
				const css = i === 0 ? ' active' : '';
				html += '<div class="location-result' + css + '" data-id="' + r.place_id + '" data-index="' + i + '">';
				html += '<span class="result-main">' + r.structured_formatting.main_text + '</span> ';
				html += '<span class="result-secondary">' + r.structured_formatting.secondary_text + '</span>';
				html += '<span class="result-description facetwp-hidden">' + r.description + '</span>';
				html += '</div>';
			});
			html += '<div class="location-attribution"><div class="powered-by-google"></div></div>';

			if (results) {
				results.innerHTML = html;
				results.classList.remove('facetwp-hidden');
			}
		}
	);
}

// — Triggers ——————————————————————————————————————————————————————

document.addEventListener('focusin', (e) => {
	if (e.target?.classList?.contains('facetwp-location')) {
		loadGoogleMaps();
	}
});

document.addEventListener('click', (e) => {
	if (e.target?.closest?.('.locate-me')) {
		loadGoogleMaps();
	}
}, true);
